import { Music, Palette, Mic, Theater, Sparkles, UtensilsCrossed, Activity, MapPin, Scissors, Leaf, Disc3, BookOpen, Gamepad2, HeartHandshake, Store, Flower2, Plane, Sailboat, Film, Dices, PartyPopper, Megaphone, GraduationCap, FileText, ShoppingBag, Soup, Briefcase } from "lucide-react";

// IDs are unaccented slugs (used in URLs and DB filtering). Labels are FR with accents.
export const CATEGORIES = [
    { id: "vide-grenier", label: "Vide-placard", icon: Store },
    { id: "exposition", label: "Exposition", icon: Palette },
    { id: "conference", label: "Conférence", icon: Mic },
    { id: "theatre", label: "Théâtre", icon: Theater },
    { id: "bons-plans", label: "Bons plans", icon: Sparkles },
    { id: "restaurant", label: "Restaurant", icon: UtensilsCrossed },
    { id: "menu-du-jour", label: "Menu du jour", icon: Soup },
    { id: "sport", label: "Sport", icon: Activity },
    { id: "decouverte", label: "Découverte", icon: MapPin },
    { id: "art-creatif", label: "Art créatif", icon: Scissors },
    { id: "green-attitude", label: "Green attitude", icon: Leaf },
    { id: "musique", label: "Musique", icon: Music },
    { id: "danse", label: "Danse", icon: Disc3 },
    { id: "litterature", label: "Littérature", icon: BookOpen },
    { id: "jeu-video", label: "Jeu vidéo", icon: Gamepad2 },
    { id: "grand-coeur", label: "Grand cœur", icon: HeartHandshake },
    { id: "marche", label: "Marché", icon: Store },
    { id: "bien-etre", label: "Bien-être", icon: Flower2 },
    { id: "voyage", label: "Voyage", icon: Plane },
    { id: "nautique", label: "Nautique", icon: Sailboat },
    { id: "cinema", label: "Cinéma", icon: Film },
    { id: "jeu", label: "Jeu", icon: Dices },
    { id: "soiree", label: "Soirée", icon: PartyPopper },
    { id: "evenementiel", label: "Événementiel", icon: Megaphone },
    { id: "formation", label: "Formation", icon: GraduationCap },
    { id: "formalites", label: "Formalités", icon: FileText },
    { id: "shopping", label: "Shopping", icon: ShoppingBag },
    { id: "emploi", label: "Emploi", icon: Briefcase },
];

export const MEAL_PERIODS = [
    { id: "matin", label: "Matin" },
    { id: "midi", label: "Midi" },
    { id: "soir", label: "Soir" },
];

// Régimes alimentaires applicables à un menu de restaurant
export const MEAL_DIETS = [
    { id: "vegetarien",     label: "Végétarien",     emoji: "🥗" },
    { id: "sans-lactose",   label: "Sans lactose",   emoji: "🥛" },
    { id: "vegan",          label: "Vegan",          emoji: "🌱" },
    { id: "sans-porc",      label: "Sans porc",      emoji: "🚫🐖" },
];

export const SPECIAL_TAGS = [
    { id: "animaux-acceptes", label: "Animaux acceptés" },
    { id: "enfants-bienvenue", label: "Enfants bienvenue" },
    { id: "special-seniors", label: "Spécial séniors" },
    { id: "special-enfants", label: "Spécial enfants (-10 ans)" },
    { id: "special-ados", label: "Spécial ados" },
    { id: "brunch", label: "Brunch" },
    { id: "cuisine-locale", label: "Cuisine locale" },
    { id: "cuisine-du-monde", label: "Cuisine du monde" },
    { id: "tea-time", label: "Tea time" },
    { id: "petit-dejeuner", label: "Petit-déjeuner" },
    { id: "diner", label: "Dîner" },
    { id: "girly", label: "Girly" },
    { id: "lgbt+", label: "LGBT+" },
];

export const ALIMENTARY = [
    { id: "vegetarien", label: "Je suis végétarien" },
    { id: "lactose-intolerant", label: "Je suis intolérant au lactose" },
    { id: "vegan", label: "Je suis vegan" },
    { id: "sans-porc", label: "Je ne mange pas de porc" },
];

export const GRADES = [
    { min: 0, name: "Le curieux du coin", level: 1 },
    { min: 5001, name: "Le testeur discoveuriste", level: 2 },
    { min: 25001, name: "L'éclaireur discoveuriste", level: 3 },
    { min: 75001, name: "L'explorateur discoveuriste", level: 4 },
    { min: 150001, name: "Le dénicheur d'expérience", level: 5 },
    { min: 250001, name: "L'épicurien aventurier", level: 6 },
    { min: 400001, name: "L'explorateur des tendances", level: 7 },
    { min: 550001, name: "Le guide des bonnes découvertes", level: 8 },
    { min: 700001, name: "Référence incontournable", level: 9 },
    { min: 850001, name: "L'ambassadeur discoveuriste", level: 10 },
];

export const REMINDERS = [
    { id: "7d", label: "7 jours avant" },
    { id: "48h", label: "48 heures avant" },
    { id: "24h", label: "24 heures avant" },
    { id: "12h", label: "12 heures avant" },
    { id: "1h", label: "1 heure avant" },
    { id: "30m", label: "30 minutes avant" },
];

export const DAYS_FR = [
    { id: "monday", label: "Lundi", color: "day-monday" },
    { id: "tuesday", label: "Mardi", color: "day-tuesday" },
    { id: "wednesday", label: "Mercredi", color: "day-wednesday" },
    { id: "thursday", label: "Jeudi", color: "day-thursday" },
    { id: "friday", label: "Vendredi", color: "day-friday" },
    { id: "saturday", label: "Samedi", color: "day-saturday" },
    { id: "sunday", label: "Dimanche", color: "day-sunday" },
];

export const formatDateFR = (iso) => {
    if (!iso) return "";
    // ISO date-only strings (YYYY-MM-DD) are timezone-agnostic dates — don't
    // let `new Date()` interpret them as UTC midnight (causes day-shift in
    // negative UTC offsets like Martinique). Parse manually.
    const s = String(iso);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
        const [, yy, mm, dd] = m;
        return `${dd}/${mm}/${yy}`;
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
};

export const gradeFromPoints = (pts) => {
    let cur = GRADES[0];
    for (const g of GRADES) if (pts >= g.min) cur = g;
    return cur;
};

export const findCategory = (id) => CATEGORIES.find(c => c.id === id);
